import LoadingButton from '@mui/lab/LoadingButton';
import { Card, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import { RHFMultiSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import {
  RHFUploadMultiFile,
  RHFUploadSingleFileAccelerator,
} from 'src/components/hook-form/rhf-upload';
import { useMyDataStore } from 'src/store/me/me.store';

type tier = {
  value: string;
  label: string;
};

type Props = {
  methods: any;
  onSubmit: VoidFunction;
  handleDrop: any;
  handleDropMultiple: any;
  handleRemove: any;
  handleRemoveAll: any;
  values: any;
  isEdit: boolean;
  isSubmitting: boolean;
  statusOptions: string[];
  statuslabel: string[];
  tierOptions: tier[];
  control: any;
  isValidEndDate: boolean;
  isValidExhibitionEndDate: boolean;
};

export default function PromotionForm({
  methods,
  onSubmit,
  handleDrop,
  handleDropMultiple,
  handleRemove,
  handleRemoveAll,
  values,
  isEdit,
  isSubmitting,
  statuslabel,
  statusOptions,
  tierOptions,
  control,
  isValidEndDate,
  isValidExhibitionEndDate,
}: Props) {
  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Card>
        <Stack spacing={2} p={4}>
          <RHFTextField name="name" label="Nome" />
          <RHFTextArea name="descriptionTitle" label="Descrição do título" />
          <RHFTextArea name="description" label="Descrição" />
          {/* <RHFSwitch name="highlight" label="Destaque" /> */}
          <RHFTextField name="location" label="Local" />
          <Typography>Imagem:</Typography>
          <RHFUploadSingleFileAccelerator name="image" acceptAll={false} onDrop={handleDrop} />
          <Typography>Banners:</Typography>
          <RHFUploadMultiFile
            name="banners"
            showPreview={false}
            onRemove={handleRemove}
            onRemoveAll={handleRemoveAll}
            onDrop={handleDropMultiple}
          />
          <Stack>
            <Stack
              direction="row"
              width="100%"
              sx={{
                alignItems: 'center',
              }}
              spacing={4}
            >
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    sx={{
                      flex: 1,
                    }}
                    closeOnSelect
                    label="Data de Início"
                    slotProps={{
                      day: {
                        sx: {
                          '&.MuiPickersDay-root.Mui-selected': {
                            backgroundColor: 'primary.main',
                          },
                        },
                      },
                    }}
                    {...field}
                  />
                )}
              />
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    sx={{
                      flex: 1,
                    }}
                    closeOnSelect
                    label="Data de Fim"
                    slotProps={{
                      day: {
                        sx: {
                          '&.MuiPickersDay-root.Mui-selected': {
                            backgroundColor: 'primary.main',
                          },
                        },
                      },
                    }}
                    {...field}
                  />
                )}
              />
            </Stack>
            {!isValidEndDate && (
              <Typography
                sx={{
                  pl: 1.8,
                  pt: 1,
                }}
                variant="caption"
                color="error"
              >
                Data de término deve ser maior ou igual do que a data de início
              </Typography>
            )}
          </Stack>
          <Controller
            name="endExhibitionDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                sx={{
                  flex: 1,
                }}
                closeOnSelect
                label="Data final de exibição"
                slotProps={{
                  day: {
                    sx: {
                      '&.MuiPickersDay-root.Mui-selected': {
                        backgroundColor: 'primary.main',
                      },
                    },
                  },
                }}
                {...field}
              />
            )}
          />
          {!isValidExhibitionEndDate && (
            <Typography
              sx={{
                pl: 1.8,
              }}
              variant="caption"
              color="error"
            >
              Data final de exibição deve ser maior ou igual do que a data de início
            </Typography>
          )}
          <RHFTextArea name="info" label="Informações" />
          <Typography>Números da sorte por nível:</Typography>
          <RHFTextField
            name="silver"
            label="Números para nível prata"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
          <RHFTextField
            name="gold"
            label="Números para nível ouro"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
          <RHFTextField
            name="diamond"
            label="Números para nível diamante"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
          <Stack spacing={1}>
            <Typography>Exibir para tiers:</Typography>
            <RHFMultiSelect name="tiers" label="Tiers" options={tierOptions} chip checkbox />
          </Stack>
          <Stack>
            <Typography>Status:</Typography>
            <RHFRadioGroupLegacy
              name="status"
              options={statusOptions}
              getOptionLabel={statuslabel}
            />
          </Stack>
          <Stack>
            <RHFTextField name="priority" label="Nível de prioridade" type="number" />
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={(isEdit && !permissions?.update) || (!isEdit && !permissions?.create)}
            >
              {!isEdit ? 'Cadastrar Sorteio' : 'Editar Sorteio'}
            </LoadingButton>
          </Stack>
        </Stack>
      </Card>
    </FormProvider>
  );
}
